'use client';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useCampaign } from '@/app/CampaignContext';
import {
  useProduct,
  useSelectedVariant,
  useAdditionalProducts,
  useSelectedQuantity
} from '@/components/product/product-context';
import { useCart } from '@/components/commerce/cart/cart-context';
import { useRouter } from 'next/navigation';
import { mergeGwpItemsIntoList } from '../add_to_cart/utils/gwp-helpers';
import { elevarAddToCart } from '@/lib/elevar/cart-events';
import { gtmAddToCart } from '@/utils/events/gtmEvents';
import { useModal, MODAL_TYPES } from '@/components/modal/ModalProvider';
import { setCypressFlag } from '@/lib/cypress/utils';
import offsetBlockClass from '@/utils/offsetBlockClass';

/**
 * The button itself.
 * This is also used for the fallback. Omitting `selectedVariantId` is what makes
 * it the fallback. The button looks the same but can't submit, and it drops
 * `data-cy` so Cypress keeps waiting for the wired-up one.
 *
 * `id="AddToCart"` stays on both because HeaderSticky measures and clicks that
 * element and SkipLink targets it. Only one of the two ever exists at a time.
 */
function SubmitButton({
  selectedVariantId,
  buttonState,
  shopifyProduct,
  selectedCrossSellProductPayload,
  variantOutOfStock
}) {
  const isShell = !selectedVariantId;
  const testProps = isShell
    ? { 'data-skeleton': '' }
    : { 'data-cy': 'add-to-cart' };

  function generateAriaLabel() {
    let productTitle = shopifyProduct.title.replace('Leesa ', '');
    const variantSize = shopifyProduct.variants.find(
      (variant) => variant.id === selectedVariantId
    )?.title;

    let priceTotal = parseInt(
      shopifyProduct.variants.find(
        (variant) => variant.id === selectedVariantId
      )?.price?.amount || 0,
      10
    );

    if (selectedCrossSellProductPayload?.length) {
      selectedCrossSellProductPayload.forEach((product) => {
        productTitle += ` & ${product.title}`;
        priceTotal +=
          parseInt(product.price.amount, 10) ||
          parseInt(product.compareAtPrice.amount, 10);
      });
    }

    const ariaLabel = `Add ${variantSize} size ${productTitle} to cart for $${priceTotal}`;
    return ariaLabel;
  }

  // `shopifyProduct.outOfStock` is static, so the shell can render this state on
  // its own; `variantOutOfStock` only arrives with the selected variant.
  if (shopifyProduct?.outOfStock || variantOutOfStock) {
    return (
      <button
        id="AddToCart"
        name="Sold"
        className="btn btn-disabled inline-block w-full"
        aria-label="Sorry, this product is out of stock"
        disabled={true}
        {...testProps}
      >
        Out of stock
      </button>
    );
  }

  return (
    <button
      id="AddToCart"
      type={isShell ? 'button' : 'submit'}
      name={buttonState.name}
      className={`btn ${buttonState.class} inline-block w-full`}
      aria-label={isShell ? buttonState.copy : generateAriaLabel()}
      disabled={buttonState.disabled}
      {...testProps}
    >
      {buttonState.copy}
    </button>
  );
}

function getButtonCopy(numItems) {
  let initialCopy;
  if (numItems > 1) {
    initialCopy = `Add ${numItems} items to cart`;
  } else {
    initialCopy = 'Add to cart';
  }
  return initialCopy;
}

function idleButtonState(numItems) {
  return {
    name: 'Add',
    copy: getButtonCopy(numItems),
    class: 'btn-primary',
    disabled: false
  };
}

/**
 * The submitting part is the only thing that needs a suspense.
 */
const AddToCartButton = ({ block, position }) => {
  const { show_offset_block_selector } =
    block.add_to_cart_button?.reference[0] || {};
  const { productData } = useProduct();
  const { additionalProducts } = useAdditionalProducts();
  const product = productData.shopifyProduct;

  return (
    <section
      id={`${block?.add_to_cart_button?.reference[0]?.uid}_${position}`}
      data-name="add-to-cart-button"
      className={offsetBlockClass(show_offset_block_selector, 'not-last:mb-8')}
    >
      <Suspense
        fallback={
          <SubmitButton
            shopifyProduct={product}
            buttonState={idleButtonState(additionalProducts.length + 1)}
          />
        }
      >
        <AddToCartForm />
      </Suspense>
    </section>
  );
};

/**
 * Reads the selected variant, so it needs to stay inside a Suspense boundary.
 * The `<form>` lives in here too: a form in the shell would have no submit
 * handler attached, so clicking the button would trigger a native GET submit.
 */
const AddToCartForm = () => {
  const { addCartItems, cart } = useCart();
  const { settings, productData } = useProduct();
  const { selectedOptions, variant } = useSelectedVariant();
  const { additionalProducts } = useAdditionalProducts();
  const { quantity } = useSelectedQuantity();
  const product = productData.shopifyProduct;
  const { variants } = product;
  const { gifts_with_purchase } = useCampaign();
  const router = useRouter();
  const currentVariant = variants.find((v) => v.id === variant.id);
  const { showModal } = useModal();

  // TODO: Temporary compatibility payload for consumers still expecting the old `state` shape.
  const legacyState = useMemo(
    () => ({
      ...selectedOptions,
      variant,
      quantity,
      additionalProducts
    }),
    [selectedOptions, variant, quantity, additionalProducts]
  );

  // Calculate if interstitial offers are in stock
  const offersInStock = useMemo(() => {
    if (!settings?.atc_interstitial?.products) return false;

    const sizeToMatch = variant?.selectedOptions.find(
      (option) => option.name === 'Size'
    )?.value;

    const inStockVariants = settings?.atc_interstitial?.products.filter(
      (card) => {
        // Find matching variant logic (simplified from original)
        const matchingVariant = card?.product?.variants.find((variant) => {
          const variantSize = variant?.selectedOptions.find(
            (option) => option.name === 'Size'
          )?.value;
          return variantSize === sizeToMatch;
        });

        return (
          matchingVariant &&
          card?.product?.outOfStock?.value !== 'true' &&
          matchingVariant?.outOfStock?.value !== 'true'
        );
      }
    );

    return inStockVariants.length > 0;
  }, [settings?.atc_interstitial?.products, variant]);

  const itemsToAdd = useMemo(() => {
    const items = [
      {
        variant: currentVariant,
        product,
        quantity: quantity || 1,
        customAttributes: [
          ...variant?.customAttributes,
          { key: '_productType', value: product.productType }
        ]
      },
      ...additionalProducts.map((item) => {
        // Here we set up a variant-like object to pass to the cart.
        // The cart wants shopify variant objects but the "type" of additional
        // products are more like line items. The most notable difference is that
        // the variant title is in a different place and it has custom attributes.
        const almostVariant = {
          ...item,
          productTitle: item.title,
          title: item.variantTitle
        };
        delete almostVariant.variantTitle;

        return {
          variant: almostVariant,
          quantity: 1,
          customAttributes: item.customAttributes ?? [],
          product: item.product
        };
      })
    ];
    return mergeGwpItemsIntoList(items, gifts_with_purchase, variant);
  }, [
    gifts_with_purchase,
    product,
    variant,
    quantity,
    additionalProducts,
    currentVariant
  ]);

  const variantOutOfStock = variant?.outOfStock;

  const [isAdding, setIsAdding] = useState(false);
  const buttonState = useMemo(() => {
    if (isAdding) {
      return {
        name: 'Adding',
        copy: 'Adding',
        class: 'btn-primary opacity-60',
        disabled: true
      };
    }

    return idleButtonState(additionalProducts.length + 1);
  }, [isAdding, additionalProducts.length]);

  useEffect(() => {
    // Reset Cypress flag on mount to accurately evaluate when adding to cart.
    // When it's null, cypress will wait until the flag is set to true or false.
    setCypressFlag('expectAtcInterstitial', null);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Always set the button state to "Adding" immediately on submit
    setIsAdding(true);

    const cartLineItemIds =
      cart?.lines?.map((lineItem) => {
        return lineItem?.merchandise?.product?.id;
      }) || [];

    let interstitialItemInCart = false;
    settings?.atc_interstitial?.products.forEach((item) => {
      if (cartLineItemIds.includes(item.product.id)) {
        interstitialItemInCart = true;
      }
    });
    const willShowInterstitial =
      settings?.atc_interstitial && !interstitialItemInCart && offersInStock;

    // Don't await the add because we want to show the interstitial immediately
    const addPromise = addCartItems(itemsToAdd, {
      suppressSuccessToast: willShowInterstitial
    });

    if (willShowInterstitial) {
      // Set Cypress flag to know to expect the interstitial
      setCypressFlag('expectAtcInterstitial', true);

      // Show the interstitial modal
      showModal(MODAL_TYPES.crossSellInterstitial, {
        state: legacyState,
        settings
      });
    }

    try {
      const { error } = await addPromise;

      setIsAdding(false);

      // The cart context already showed the error toast, so just stay put.
      if (error) {
        return;
      }

      elevarAddToCart(itemsToAdd);
      gtmAddToCart({ itemsToAdd: itemsToAdd, page: 'Page' });
      /*  
          Trigger the personalize event
          await personalize.triggerEvent('AddToCart');
          */
      if (!willShowInterstitial) {
        // Signal that we do not expect an interstitial
        setCypressFlag('expectAtcInterstitial', false);

        // Then navigate to the cart
        router.push('/cart');
      }
    } catch (error) {
      console.error('Error adding items to cart:', error);
      setIsAdding(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <SubmitButton
        shopifyProduct={product}
        selectedVariantId={currentVariant.id}
        buttonState={buttonState}
        selectedCrossSellProductPayload={additionalProducts}
        variantOutOfStock={variantOutOfStock}
      />
    </form>
  );
};

export default AddToCartButton;
